import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { RagChip } from "@/components/RagChip";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  ArrowLeft, Trophy, AlertTriangle, Link2, LifeBuoy, Target, Loader2, CalendarClock,
  ChevronDown, ChevronUp, History, Info, ShieldAlert, ShieldOff, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

const NARR = [
  { key: "achievements", title: "Key Achievements This Month", icon: Trophy, color: "#107C41" },
  { key: "challenges", title: "Challenges", icon: AlertTriangle, color: "#D97706" },
  { key: "blockers", title: "Dependencies / Blockers", icon: Link2, color: "#A80000" },
  { key: "support_required", title: "Support Required", icon: LifeBuoy, color: "#005A9E" },
  { key: "next_month_focus", title: "Planned Focus Next Month", icon: Target, color: "#008272" },
];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [sp] = useSearchParams();
  const asOf = sp.get("as_of") || "latest";
  const navigate = useNavigate();
  const { session } = useSession();
  const isAdmin = session?.role === "pmo_admin";
  const qc = useQueryClient();
  const [openMonth, setOpenMonth] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [removing, setRemoving] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["project", session?.persona, projectId, asOf],
    queryFn: async () => (await api.get(`/projects/${projectId}`, { params: { as_of: asOf } })).data,
    retry: false,
  });

  const { data: audit } = useQuery({
    queryKey: ["audit", projectId, showAudit],
    queryFn: async () => (await api.get("/admin/audit", { params: { project_id: projectId } })).data,
    enabled: isAdmin && showAudit,
  });

  const removeOverride = async (monthKey) => {
    setRemoving(true);
    try {
      await api.post(`/projects/${projectId}/override/remove`, { month_key: monthKey });
      toast.success("Override removed — reverted to calculated health");
      qc.invalidateQueries({ queryKey: ["project"] });
      qc.invalidateQueries({ queryKey: ["audit"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove override");
    } finally { setRemoving(false); }
  };

  useEffect(() => { if (data?.selected_month) setOpenMonth(data.selected_month); }, [data?.selected_month]);

  if (isLoading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#0078D4]" size={26} /></div>;
  if (isError || !data) return (
    <div className="fluent-card p-8 text-center" data-testid="project-not-found">
      <p className="text-[#A80000] font-semibold">Not found or outside your scope.</p>
      <button onClick={() => navigate("/")} className="text-[#0078D4] text-sm mt-3 font-semibold">← Back to Portfolio Overview</button>
    </div>
  );

  const p = data.project;

  return (
    <div className="space-y-5 fluent-enter" data-testid="project-detail-page">
      <button onClick={() => navigate("/")} data-testid="back-to-portfolio" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0078D4] hover:text-[#004578] fluent-focus rounded">
        <ArrowLeft size={15} /> Back to Portfolio Overview
      </button>

      <div className="fluent-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-[#201F1E]" data-testid="detail-project-name">{p.project_name}</h1>
            <p className="text-sm text-[#605E5C] mt-1">{p.platform} · {p.tech_stack} · Selected month: {p.month_label}{p.carried_forward && " (carried forward)"}</p>
            <p className="text-sm text-[#323130] mt-3 max-w-3xl">{p.about}</p>
          </div>
          <div className="text-right">
            <RagChip rag={p.rag} testid="detail-rag"
              isOverridden={p.is_overridden} overrideBy={p.override_by} overrideAt={p.override_at}
              preRag={p.pre_override_rag} overrideReason={p.override_reason}
              catchupAmber={p.catchup_amber} catchupGreen={p.catchup_green} />
            <div className="text-[10px] text-[#605E5C] mt-1 flex items-center gap-1 justify-end"><Info size={11} /> Project Health</div>
          </div>
        </div>
        {p.is_overridden && (
          <div className="mt-4 rounded border border-[#C7D2FE] bg-[#EEF2FF] p-3 text-xs text-[#3730A3] flex items-start justify-between gap-3" data-testid="override-banner">
            <div className="flex items-start gap-2">
              <ShieldAlert size={15} className="shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Health manually overridden to {p.rag === "amber" ? "Amber" : p.rag === "green" ? "Green" : "Not Rated"}.</span>
                {" "}{p.override_reason}
                <div className="text-[11px] mt-0.5 opacity-80">By {p.override_by || "admin"}{p.override_at ? ` on ${String(p.override_at).slice(0, 10)}` : ""} · original: {p.pre_override_rag || "—"}</div>
              </div>
            </div>
            {isAdmin && (
              <Button variant="outline" size="sm" className="h-8 text-xs shrink-0 fluent-focus" data-testid="remove-override-btn"
                disabled={removing} onClick={() => removeOverride(p.month_key)}>
                <ShieldOff size={13} className="mr-1.5" /> Remove override
              </Button>
            )}
          </div>
        )}
        <div className="mt-4 rounded bg-[#F3F2F1] border border-[#E1DFDD] p-3 text-xs text-[#323130]" data-testid="rag-reason">
          <span className="font-semibold text-[#201F1E]">Health rationale: </span>{p.rag_reason}
          <div className="text-[11px] text-[#605E5C] mt-1">{data.rules_basis}</div>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Field label="% Completion"><div className="flex items-center gap-2"><Progress value={p.pct_completion || 0} className="h-2 flex-1" /><span className="font-semibold">{p.pct_completion ?? "—"}%</span></div></Field>
        <Field label="Expected % (schedule)"><span className="font-semibold">{p.expected_pct ?? "—"}%</span></Field>
        <Field label="Start Date"><span className="font-semibold">{p.start_date || "Not reported"}</span></Field>
        <Field label="End Date"><span className="font-semibold inline-flex items-center gap-1">{p.end_date || "Not reported"}{p.overdue && <CalendarClock size={13} className="text-[#A80000]" />}</span></Field>
        <Field label="Bandwidth / Availability"><span className="font-semibold">{p.bandwidth || "Not reported"}</span></Field>
        <Field label="Contributor"><span className="font-semibold">{p.contributor_name}</span><div className="text-[11px] text-[#605E5C]">{p.contributor_id}</div></Field>
        <Field label="Platform / Product"><span className="font-semibold">{p.platform}</span></Field>
        <Field label="Schedule gap"><span className="font-semibold">{p.gap ?? "—"}{p.gap != null ? " pts" : ""}</span></Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {NARR.map((n) => {
          const Icon = n.icon; const val = p[n.key];
          return (
            <div key={n.key} className="fluent-card p-4" data-testid={`narrative-${n.key}`}>
              <div className="flex items-center gap-2 mb-2"><Icon size={15} style={{ color: n.color }} /><h3 className="text-sm font-semibold text-[#201F1E]">{n.title}</h3></div>
              <p className="text-sm text-[#323130] leading-relaxed">{val && val.trim() ? val : <span className="text-[#A19F9D] italic">Not reported.</span>}</p>
            </div>
          );
        })}
      </div>

      {/* Monthly History */}
      <div className="fluent-card p-5" data-testid="monthly-history">
        <div className="flex items-center gap-2 mb-3"><History size={16} className="text-[#0078D4]" /><h3 className="text-sm font-semibold text-[#201F1E]">Monthly History</h3><span className="text-[11px] text-[#605E5C]">({data.history.length} reporting month{data.history.length !== 1 ? "s" : ""}, newest first)</span></div>
        <div className="space-y-2">
          {data.history.map((h, i) => (
            <div key={h.month_key} className="border border-[#E1DFDD] rounded overflow-hidden" data-testid={`history-item-${i}`}>
              <button onClick={() => setOpenMonth(openMonth === h.month_key ? null : h.month_key)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[#F3F9FD] fluent-focus" data-testid={`history-toggle-${h.month_key}`}>
                <div className="flex items-center gap-3">
                  <RagChip rag={h.rag} />
                  <span className="font-semibold text-[#201F1E] text-sm">{h.month_label}</span>
                  <span className="text-xs text-[#605E5C]">{h.pct_completion ?? "—"}% · rev v{h.revision}</span>
                </div>
                {openMonth === h.month_key ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {openMonth === h.month_key && (
                <div className="px-4 py-3 bg-[#FAFAFA] border-t border-[#EDEBE9]" data-testid={`history-panel-${h.month_key}`}>
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <Field label="Key Achievements">{h.achievements}</Field>
                    <Field label="Challenges">{h.challenges}</Field>
                    <Field label="Dependencies / Blockers">{h.blockers}</Field>
                    <Field label="Support Ask">{h.support_required}</Field>
                    <Field label="Planned Focus">{h.next_month_focus}</Field>
                    <Field label="Bandwidth">{h.bandwidth}</Field>
                  </div>
                  <div className="text-[11px] text-[#605E5C] mt-2">{h.rag_reason} · source: {h.source || "n/a"}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Override History (admin only) */}
      {isAdmin && (
        <div className="fluent-card p-5" data-testid="override-history">
          <button onClick={() => setShowAudit((s) => !s)} data-testid="override-history-toggle"
            className="w-full flex items-center justify-between gap-2 fluent-focus rounded">
            <span className="flex items-center gap-2"><ClipboardList size={16} className="text-[#4338CA]" />
              <h3 className="text-sm font-semibold text-[#201F1E]">Override History</h3></span>
            {showAudit ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showAudit && (
            <div className="mt-3">
              {!audit ? (
                <div className="text-sm text-[#605E5C] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
              ) : (audit.events || []).length === 0 ? (
                <p className="text-sm text-[#605E5C]" data-testid="audit-empty">No override events recorded for this project.</p>
              ) : (
                <div className="space-y-2">
                  {audit.events.map((e, i) => (
                    <div key={i} className="border border-[#E1DFDD] rounded px-3 py-2 text-xs" data-testid={`audit-row-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-[#201F1E]">
                          {e.action === "remove" ? "Override removed" : "Override set"} · {e.month_key} · {e.from_rag} → {e.to_rag}
                        </span>
                        <span className="text-[#605E5C]">{e.timestamp ? String(e.timestamp).slice(0, 16).replace("T", " ") : ""}</span>
                      </div>
                      <div className="text-[#323130] mt-0.5">{e.reason}</div>
                      <div className="text-[11px] text-[#605E5C] mt-0.5">by {e.actor}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="fluent-card p-5" data-testid="detail-trend">
        <h3 className="text-sm font-semibold text-[#201F1E] mb-4">Month-over-Month % Completion</h3>
        {data.trend.length <= 1 ? (
          <p className="text-sm text-[#605E5C]">Only one month of data for this project so far.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0078D4" stopOpacity={0.35} /><stop offset="95%" stopColor="#0078D4" stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E1DFDD" vertical={false} />
              <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: "#605E5C" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#605E5C" }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="pct_completion" name="Actual %" stroke="#0078D4" fill="url(#g)" strokeWidth={2} />
              <Area type="monotone" dataKey="expected_pct" name="Expected %" stroke="#008272" fill="none" strokeWidth={2} strokeDasharray="5 4" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  const v = children && String(children).trim() ? children : null;
  return (
    <div className="fluent-card p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#605E5C] mb-1">{label}</div>
      <div className="text-sm text-[#323130]">{v || <span className="text-[#A19F9D] italic">Not reported</span>}</div>
    </div>
  );
}
